import type { StrategyNode } from '../types/BaseNode'
import { CompositeNode } from '../types/CompositeNode'
import { FormatterNode } from '../types/FormatterNode'
import { PnLNode } from '../types/PnLNode'
import { VarianceNode } from '../types/VarianceNode'
import { MeepMeepNode } from '../types/MeepMeepNode'
import { RegressionNode } from '../types/RegressionNode'
import { v4 as uuidv4 } from 'uuid'
import { getDefaultOperatorStatic } from '../hooks/useOperators'
import { getDefaultCombineOnStatic } from '../hooks/useCombineOptions'

// 🔁 Replace a node by ID
export function replaceNodeInTree(root: StrategyNode, targetId: string, replacement: StrategyNode): StrategyNode {
  if (root.id === targetId) {
    // Prevent unnecessary tree updates if node is identical
    if (
      root === replacement || (
        root.title === replacement.title &&
        root.type === replacement.type &&
        JSON.stringify(root.config) === JSON.stringify(replacement.config)
      )
    ) {
      return root;
    }
    return replacement;
  }




  if (root.type === 'Composite') {
    const updated = new CompositeNode(root.id, root.title, root.config.operator, root.config.combine_on, undefined, undefined, root.isManualRename);
    updated.config.left = root.config.left ? replaceNodeInTree(root.config.left, targetId, replacement) : undefined;
    updated.config.right = root.config.right ? replaceNodeInTree(root.config.right, targetId, replacement) : undefined;
    return updated;
  }
  if (root.type === 'Formatter') {
    const { typeId, props, child } = root.config;
    const updated = new FormatterNode(root.id, root.title, root.isManualRename);
    updated.config = {
      typeId,
      props,
      child: child ? replaceNodeInTree(child, targetId, replacement) : null
    };
    return updated;
  }
  if (root.type === 'PnL') {
    const updated = new PnLNode(root.id, root.title, root.config.entry, root.config.exit, root.config.maxLots, root.config.lotSize, root.config.fees, root.config.mode, root.isManualRename);
    updated.config.entry = root.config.entry ? replaceNodeInTree(root.config.entry, targetId, replacement) : undefined;
    updated.config.exit = root.config.exit ? replaceNodeInTree(root.config.exit, targetId, replacement) : undefined;
    return updated;
  }
  if (root.type === 'Variance') {
    const updated = new VarianceNode(root.id, root.title,
      root.config.target ? replaceNodeInTree(root.config.target, targetId, replacement) : undefined,
      (root.config.parameters ?? []).map((p: StrategyNode) => replaceNodeInTree(p, targetId, replacement)),
      root.isManualRename
    );
    return updated;
  }
  if (root.type === 'MeepMeep') {
    return new MeepMeepNode(
      root.id, root.title,
      root.config.main_market ? replaceNodeInTree(root.config.main_market, targetId, replacement) : undefined,
      root.config.signal ? replaceNodeInTree(root.config.signal as StrategyNode, targetId, replacement) as any : undefined,
            root.config.latency_ms,
      root.config.insample_days,
      root.config.target_forward_ticks,
      root.config.max_volume,
      root.config.trade_margin,
      root.config.trade_margin_regime,
      root.isManualRename
    );
  }
  if (root.type === 'Regression') {
    return new RegressionNode(
      root.id, root.title,
      root.config.model_type,
      [...root.config.weights],
      (root.config.inputs ?? []).map((p: StrategyNode) => replaceNodeInTree(p, targetId, replacement)),
      root.isManualRename,
      root.config.last_calibrated_at,
      root.config.calibration_artifact_id,
      root.config.calibration_artifact_path,
      root.config.feature_tree_hash,
      root.config.feature_labels
    );
  }
  return root;
}

// ❌ Delete a node by ID (returns pruned tree or undefined)
export function deleteNodeInTree(root: StrategyNode, targetId: string): StrategyNode | undefined {
  if (root.id === targetId) return undefined;

  if (root.type === 'Composite') {
    const updated = new CompositeNode(root.id, root.title, root.config.operator, root.config.combine_on, undefined, undefined, root.isManualRename);
    updated.config.left = root.config.left ? deleteNodeInTree(root.config.left, targetId) : undefined;
    updated.config.right = root.config.right ? deleteNodeInTree(root.config.right, targetId) : undefined;
    return updated;
  }
  if (root.type === 'Formatter') {
    const { typeId, props, child } = root.config;
    const updated = new FormatterNode(root.id, root.title, root.isManualRename);
    updated.config = {
      typeId,
      props,
      child: child ? deleteNodeInTree(child, targetId) ?? null : null
    };
    return updated;
  }
  if (root.type === 'PnL') {
    const updated = new PnLNode(root.id, root.title, root.config.entry, root.config.exit, root.config.maxLots, root.config.lotSize, root.config.fees, root.config.mode, root.isManualRename);
    updated.config.entry = root.config.entry ? deleteNodeInTree(root.config.entry, targetId) : undefined;
    updated.config.exit = root.config.exit ? deleteNodeInTree(root.config.exit, targetId) : undefined;
    return updated;
  }
  if (root.type === 'Variance') {
    const updated = new VarianceNode(root.id, root.title,
      root.config.target ? deleteNodeInTree(root.config.target, targetId) : undefined,
      (root.config.parameters ?? []).map((p: StrategyNode) => deleteNodeInTree(p, targetId)).filter(Boolean) as StrategyNode[],
      root.isManualRename
    );
    return updated;
  }
  if (root.type === 'MeepMeep') {
    return new MeepMeepNode(
      root.id, root.title,
      root.config.main_market ? deleteNodeInTree(root.config.main_market, targetId) : undefined,
      root.config.signal ? deleteNodeInTree(root.config.signal as StrategyNode, targetId) as any : undefined,
            root.config.latency_ms,
      root.config.insample_days,
      root.config.target_forward_ticks,
      root.config.max_volume,
      root.config.trade_margin,
      root.config.trade_margin_regime,
      root.isManualRename
    );
  }
  if (root.type === 'Regression') {
    return new RegressionNode(
      root.id, root.title,
      root.config.model_type,
      [...root.config.weights],
      (root.config.inputs ?? []).map((p: StrategyNode) => deleteNodeInTree(p, targetId)).filter(Boolean) as StrategyNode[],
      root.isManualRename,
      root.config.last_calibrated_at,
      root.config.calibration_artifact_id,
      root.config.calibration_artifact_path,
      root.config.feature_tree_hash,
      root.config.feature_labels
    );
  }

  return root;
}

// 🪄 Wrap a node inside a new parent Composite
export function wrapNodeInComposite(root: StrategyNode, idToWrap: string): StrategyNode {
  const target = findNodeById(root, idToWrap);
  if (!target) return root;

  const newParent = new CompositeNode(uuidv4(), undefined, getDefaultOperatorStatic(), getDefaultCombineOnStatic());
  newParent.config.left = target;
  newParent.config.right = undefined;

  return replaceNodeInTree(root, idToWrap, newParent);
}

export function wrapNodeInFormatter(root: StrategyNode, idToWrap: string): StrategyNode {
  const target = findNodeById(root, idToWrap);
  if (!target) return root;

  const newParent = new FormatterNode(uuidv4());
  newParent.config.typeId = "";
  newParent.config.child = target;

  return replaceNodeInTree(root, idToWrap, newParent);
}

export function promoteNodeToParent(root: StrategyNode, idToPromote: string): StrategyNode {
  const parent = findParentOfNode(root, idToPromote);
  if (!parent) return root; // can't promote if no parent

  const nodeToPromote = findNodeById(root, idToPromote);
  if (!nodeToPromote) return root;

  const grandparent = findParentOfNode(root, parent.id);

  if (!grandparent) {
    // Parent is the root — so promote to root
    return nodeToPromote;
  }

  // Replace parent with promoted node in grandparent
  return replaceNodeInTree(root, parent.id, nodeToPromote);
}




// 🔍 Find a node by ID
export function findNodeById(root: StrategyNode, id: string): StrategyNode | undefined {
  if (root.id === id) return root;

  if (root.type === 'Composite') {
    return (
      (root.config.left && findNodeById(root.config.left, id)) ||
      (root.config.right && findNodeById(root.config.right, id)) ||
      undefined
    );
  }
  if (root.type === 'Formatter') {
    return root.config.child ? findNodeById(root.config.child, id) : undefined;
  }
  if (root.type === 'PnL') {
    return (
      (root.config.entry && findNodeById(root.config.entry, id)) ||
      (root.config.exit && findNodeById(root.config.exit, id)) ||
      undefined
    );
  }
  if (root.type === 'Variance') {
    return (
      (root.config.target && findNodeById(root.config.target, id)) ||
      (root.config.parameters ?? []).reduce((found: StrategyNode | undefined, p: StrategyNode) => found ?? findNodeById(p, id), undefined)
    );
  }
  if (root.type === 'MeepMeep') {
    return (
      (root.config.main_market && findNodeById(root.config.main_market, id)) ||
      (root.config.signal && findNodeById(root.config.signal as StrategyNode, id)) ||
      undefined
    );
  }
  if (root.type === 'Regression') {
    return (root.config.inputs ?? []).reduce(
      (found: StrategyNode | undefined, p: StrategyNode) => found ?? findNodeById(p, id),
      undefined
    );
  }

  return undefined;
}

export function findParentOfNode(node: StrategyNode, targetId: string): StrategyNode | null {
  if (node.type === 'Composite') {
    for (const child of [node.config.left, node.config.right]) {
      if (child?.id === targetId) {
        return node;
      }
      if (child) {
        const found = findParentOfNode(child, targetId);
        if (found) return found;
      }
    }
  }
  if (node.type === 'Formatter') {
    for (const child of [node.config.child]) {
      if (child?.id === targetId) {
        return node;
      }
      if (child) {
        const found = findParentOfNode(child, targetId);
        if (found) return found;
      }
    }
  }
  if (node.type === 'PnL') {
    for (const child of [node.config.entry, node.config.exit]) {
      if (child?.id === targetId) {
        return node;
      }
      if (child) {
        const found = findParentOfNode(child, targetId);
        if (found) return found;
      }
    }
  }
  if (node.type === 'Variance') {
    for (const child of [node.config.target, ...(node.config.parameters ?? [])]) {
      if (child?.id === targetId) return node;
      if (child) {
        const found = findParentOfNode(child, targetId);
        if (found) return found;
      }
    }
  }
  if (node.type === 'MeepMeep') {
    for (const child of [node.config.main_market, node.config.signal as StrategyNode | undefined]) {
      if (child?.id === targetId) return node;
      if (child) {
        const found = findParentOfNode(child, targetId);
        if (found) return found;
      }
    }
  }
  if (node.type === 'Regression') {
    for (const child of (node.config.inputs ?? []) as StrategyNode[]) {
      if (child?.id === targetId) return node;
      if (child) {
        const found = findParentOfNode(child, targetId);
        if (found) return found;
      }
    }
  }

  return null;
}



